import { useForm } from '@deadcow-enterprises/react-forms';
import type { CodeInputProps } from '@/components/code-input/props';
import type { ContactType } from '@/components/contact-selection/props';
import { ErrorSidePanel } from '@/components/errors';
import { useSendCode, useValidateCode } from '@/hooks/api/contact-validation.hooks';
import { loadHistoryState } from '@/lib';
import {
  ErrorCodes,
  logger,
  templates,
  useI18n,
  widgets,
  type SkinPageConfig,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import z from 'zod';
import { CodeInputLight } from '@/components/code-input/code-input.light';
import { CodeInputWoo } from '@/components/code-input/code-input.woo';
import { CodeInputWooDark } from '@/components/code-input/code-input.woo-dark';
import { profileCodeInputStateSchema } from '../-model/flow';

export const Route = createFileRoute(
  '/(tv)/tv/$id/(tv-profiles)/_sheet/profiles/$profileId/update/code-input/',
)({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  loader: loadHistoryState(profileCodeInputStateSchema, { errorCode: ErrorCodes.AccessDenied }),
});

function RouteComponent() {
  const { contactType, targetId } = Route.useLoaderData();

  return <CodeInputForm contactType={contactType as ContactType} targetId={targetId} />;
}

function CodeInputForm({
  contactType,
  targetId,
}: Readonly<{ contactType: ContactType; targetId: string }>) {
  const { id, profileId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { t } = useI18n();

  const form = useForm<{ code: string }>(
    {
      code: {
        value: '',
        id: 'profile-contact-validation-otp',
        validator: z
          .string()
          .length(
            widgets.ValidationCodeLength,
            t('Common.ContactValidation.CodeInput.Errors.InvalidCode').toString(),
          ),
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  const { sendCode, isSending, captcha } = useSendCode();
  const { validateCode, isValidating } = useValidateCode({
    onSuccess: (securityCode) => {
      void navigate({
        to: '/tv/$id/profiles/$profileId/update',
        params: { id, profileId },
        state: { securityCode },
      });
    },
    onError: () => {
      form.addError('code', t('Common.ContactValidation.CodeInput.Errors.InvalidCode').toString());
      form.setValue('code', '');
    },
  });

  const handleResend = () => {
    form.setValue('code', '');
    if (captcha.challenge && !captcha.token) return;
    sendCode({
      targetId,
      type: contactType,
      resend: true,
      captcha,
    });
  };

  const handleSubmit = ({ code }: typeof form.value) => {
    validateCode({ code, targetId, contactType });
  };

  const handleCodeChange = (value: string) => {
    form.setValue('code', value);
    if (value.length === widgets.ValidationCodeLength) {
      form.updateValidity();
      validateCode({ code: value, targetId, contactType });
    }
  };

  const props: CodeInputProps = {
    codeValue: form.value.code,
    codeId: form.ids.code ?? 'profile-contact-validation-otp',
    codeErrors: form.errors.code,
    isValidating,
    isSending,
    captchaChallenge: captcha.challenge,
    onCaptchaChange: captcha.setToken,
    onCodeChange: handleCodeChange,
    onResend: handleResend,
    onSubmit: form.handleSubmit(handleSubmit),
  };

  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: CodeInputProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <CodeInputWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <CodeInputWoo {...props} />,
      },
    ],
    defaultValue: <CodeInputLight {...props} />,
  };
}
